// @ts-strict-ignore
/** Until insights uses mp-line-chart any changes in here will also
 * need to be made in iron/apps/insights/src/insights-app/insights-result/chart-display/line-chart/mp-line-chart/mixins/annotations.js */
import moment from 'moment-timezone';
import sortedLastIndex from 'lodash/sortedLastIndex';
import type {Annotation as HigchartsAnnotation} from 'highcharts';

import {getProjectId, getReportName, getWorkspaceId} from 'common/report/util';
import {annotationsClient} from 'common/report/queries/app-api/annotations-client';
import {offsetForHighcharts} from 'common/util/date';
import {formatDate, MS_BY_UNIT} from 'common/util/date';

import {Annotation, AnnotationState} from '../types';
import type {LineChart} from '../index';
import {ButtonLocationDetails, HighChartXAxis} from './type';
import {track, TrackingEvent, TrackingEventProperty} from './tracking';
// Import the create annotation button component so it can be used in the annotation popup
import './create-annotation-button';
import {
  ANNOTATION_GROUP_BUTTON_ID_PREFIX,
  ANNOTATION_PLOT_LINE_ID,
  CREATE_ANNOTATION_ID,
  EXISTING_ANNOTATIONS_BUTTON_Y,
  HOVER_PLOT_LINE_ID,
} from './constants';

export class Annotations {
  private declare ticks: string[];

  constructor(public lineChart: LineChart) {}

  static getHelpers(lineChart: LineChart) {
    return {
      add: async (annotation: Annotation) => {
        await lineChart.annotationsRenderer.process([annotation]);
        await lineChart.annotationsRenderer.closePopup();
      },
      close: async () => {
        await lineChart.annotationsRenderer.closePopup();
      },
      delete: async ({detail: annotation}: CustomEvent) =>
        lineChart.annotationsRenderer.updateLineChartState({deletingAnnotation: annotation}),
      deleteCanceled: async () => lineChart.annotationsRenderer.updateLineChartState({deletingAnnotation: null}),
      deleteConfirmed: async () => {
        const projectId = parseInt(getProjectId());
        const workspaceId = parseInt(getWorkspaceId());
        const timeIndex = lineChart.annotationsRenderer.lineChart.state.annotations.activeAnnotationButton.timestampMs;
        const {deletingAnnotation} = lineChart.annotationsRenderer.lineChart.state.annotations;

        await annotationsClient.deleteAnnotation({projectId, id: deletingAnnotation.id, workspaceId});

        const annotationRemaining = lineChart.annotationsRenderer.lineChart.state.annotations.allAnnotations
          .get(timeIndex)
          .filter((annotation) => annotation.id !== deletingAnnotation.id);

        const allAnnotations = lineChart.annotationsRenderer.lineChart.state.annotations.allAnnotations;

        if (annotationRemaining.length === 0) {
          allAnnotations.delete(timeIndex);
        } else {
          allAnnotations.set(timeIndex, annotationRemaining);
        }

        await lineChart.annotationsRenderer.updateLineChartState({allAnnotations});

        await lineChart.annotationsRenderer.setAnnotations();

        if (annotationRemaining.length === 0) {
          lineChart.annotationsRenderer.closePopup();
        }

        await lineChart.annotationsRenderer.updateLineChartState({deletingAnnotation: null});

        track(TrackingEvent.Delete, {
          [TrackingEventProperty.AnnotationText]: deletingAnnotation.description,
          [TrackingEventProperty.AnnotationTimestamp]: moment(deletingAnnotation.date).format(`YYYY-MM-DD HH:mm:ss`),
          [TrackingEventProperty.ReportName]: getReportName(),
        });
      },
      edit: async ({detail: {editingAnnotation, annotationDescriptionDraft}}: CustomEvent) => {
        await lineChart.annotationsRenderer.updateLineChartState({editingAnnotation, annotationDescriptionDraft});
      },
      editCanceled: async () => lineChart.annotationsRenderer.updateLineChartState({editingAnnotation: null}),
      popupChange: (ev) => {
        if (ev.detail && ev.detail.state === `closed`) {
          lineChart.annotationsRenderer.popupClosed();
        }
      },
      save: async () => {
        const {editingAnnotation} = lineChart.annotationsRenderer.lineChart.state.annotations;
        if (editingAnnotation) {
          const {allAnnotations, annotationDescriptionDraft} =
            lineChart.annotationsRenderer.lineChart.state.annotations;
          const projectId = parseInt(getProjectId());
          const workspaceId = parseInt(getWorkspaceId());
          const {id} = editingAnnotation;
          await annotationsClient.updateAnnotation({
            projectId,
            id,
            description: annotationDescriptionDraft,
            workspaceId,
          });
          editingAnnotation.description = annotationDescriptionDraft;

          // Update the annotation that we've saved to show immediately
          allAnnotations.forEach((value) =>
            value.forEach((entry) => {
              if (entry.id === id) {
                entry.description = annotationDescriptionDraft;
              }
            }),
          );

          await lineChart.annotationsRenderer.updateLineChartState({editingAnnotation: null, allAnnotations});
          await lineChart.annotationsRenderer.setAnnotations();

          track(TrackingEvent.Edit, {
            [TrackingEventProperty.AnnotationText]: editingAnnotation.description,
            [TrackingEventProperty.AnnotationTimestamp]: moment(editingAnnotation.date).format(`YYYY-MM-DD HH:mm:ss`),
            [TrackingEventProperty.ReportName]: getReportName(),
          });
        }
      },
      showCreate: async () =>
        lineChart.annotationsRenderer.showCreateAnnotation(
          lineChart.annotationsRenderer.lineChart.state.annotations.activeAnnotationButton,
        ),
      updateDescriptionDraft: async ({detail}: CustomEvent) =>
        lineChart.annotationsRenderer.updateLineChartState({annotationDescriptionDraft: detail}),
    };
  }

  static getDefaultState(): any {
    return {
      activeAnnotationButton: null,
      creatingAnnotation: false,
      activeAnnotationPopupClosing: false,
      createAnnotationDate: ``,
      visibleAnnotations: new Map<number, any>(),
      allAnnotations: new Map<number, any>(),
      activeAnnotationButtonTimestamp: null,
      editingAnnotation: null,
      viewingAnnotations: false,
      activeAnnotationPreview: null,
      annotationDescriptionDraft: null,
      deletingAnnotation: null,
    };
  }

  private async closePopup() {
    await this.updateLineChartState({activeAnnotationPopupClosing: true});
  }

  areLoaded() {
    return Boolean(this.ticks);
  }

  private getXAxisMinAndMax(): HighChartXAxis {
    const {dataMax, dataMin} = this.lineChart.getHighcharts().xAxis[0].getExtremes();
    const maxDateMs = dataMax + MS_BY_UNIT[this.lineChart.props.timeUnit];
    return {minDateMs: dataMin, maxDateMs};
  }

  async load() {
    // we rely on timestamps generated from the series so we don't need to deal with timezones and whatnot
    const firstSeries = this.lineChart.getHighcharts().series[0];
    if (!firstSeries) {
      return;
    }

    // @ts-ignore Property 'processedXData' does not exist on type 'Series'.
    this.ticks = firstSeries.processedXData.map((num) => num.toString());
    const xAxis = this.getXAxisMinAndMax();
    const format = (timestamp) => moment(timestamp).tz(this.lineChart.props.timezone).format(`YYYY-MM-DD`);

    const annotations = await annotationsClient.query({
      projectId: parseInt(getProjectId()),
      fromDate: format(xAxis.minDateMs),
      toDate: format(xAxis.maxDateMs),
      workspaceId: parseInt(getWorkspaceId()),
    });

    // check again if highcharts is still initialized; by the time the annotationsClient query has finished, highcharts may have been destroyed
    // If there was an error fetching annotations we don't want to process them
    if (this.lineChart.getHighcharts() && annotations) {
      this.process(annotations);
    }
  }

  private locationDetails(annotation: HigchartsAnnotation, timestampMs: number): ButtonLocationDetails {
    return {
      element: annotation.labelsGroup.element,
      formattedDate: this.timestampToTimeUnitFunction()(timestampMs),
      timestampMs,
    };
  }

  async popupClosed() {
    await this.updateLineChartState({
      viewingAnnotations: false,
      activeAnnotationButton: null,
      creatingAnnotation: false,
      activeAnnotationPopupClosing: false,
    });
  }

  /**
   * Group annotations by the nearest chart timestamp that is less than the
   * annotation timestamp, sort them in ascending order, and update the app state
   */
  async process(annotationsToProcess: Annotation[]) {
    const utcOffset = this.lineChart.props.utcOffset;
    const projectTimezone = this.lineChart.props.timezone;

    // Don't mutate the original map
    const allAnnotations = new Map(this.lineChart.state.annotations.allAnnotations);

    annotationsToProcess.forEach((annotation) => {
      annotation.highchartsDate = offsetForHighcharts(moment(annotation.date), projectTimezone, utcOffset);

      const annotationDateMS = moment.utc(annotation.highchartsDate).valueOf();
      const index = parseInt(this.ticks[sortedLastIndex(this.ticks, annotationDateMS.toString()) - 1]);

      if (allAnnotations.has(index)) {
        const annotationsAtTime = allAnnotations.get(index);
        annotationsAtTime.push(annotation);
        allAnnotations.set(index, annotationsAtTime);
      } else {
        allAnnotations.set(index, [annotation]);
      }
    });

    await this.updateLineChartState({allAnnotations});
    await this.setAnnotations();
  }

  async setAnnotations() {
    const xAxis = this.getXAxisMinAndMax();
    // Don't mutate the original map
    const visibleAnnotations = new Map(this.lineChart.state.annotations.allAnnotations);

    visibleAnnotations.forEach((_annotation, date) => {
      if (date < xAxis.minDateMs || date > xAxis.maxDateMs) {
        visibleAnnotations.delete(date);
      }
    });

    await this.updateLineChartState({visibleAnnotations});
    this.redrawGroupButtons();
  }

  redrawCreateButton() {
    if (this.lineChart.props.downloadMode) return;

    this.removePlotLine(HOVER_PLOT_LINE_ID);
    this.removeAnnotation(CREATE_ANNOTATION_ID);

    const hoveredTimestamp = this.lineChart.getHoveredTimestampMs();

    if (
      this.lineChart.props.showAnnotations &&
      hoveredTimestamp &&
      !this.lineChart.state.annotations.visibleAnnotations.has(this.lineChart.getHoveredTimestampMs())
    ) {
      this.renderCreateAnnotationButton(hoveredTimestamp, false);
      this.renderPlotLine(hoveredTimestamp, HOVER_PLOT_LINE_ID);
    }
  }

  private redrawGroupButtons() {
    if (this.lineChart.props.downloadMode) return;
    this.lineChart.state.annotations.visibleAnnotations.forEach((_annotations, timestampMsStr) => {
      this.removeAnnotation(`${ANNOTATION_GROUP_BUTTON_ID_PREFIX}-${timestampMsStr}`);
    });
    this.removePlotLine(ANNOTATION_PLOT_LINE_ID);

    this.lineChart.state.annotations.visibleAnnotations.forEach((annotations, timestampMsStr) => {
      const timestampMs = Number(timestampMsStr);
      this.renderViewAnnotationsButton(
        timestampMs,
        this.lineChart.state.annotations.activeAnnotationButtonTimestamp === timestampMs,
        annotations.length,
      );
      this.renderPlotLine(timestampMs, ANNOTATION_PLOT_LINE_ID);
    });
  }

  private removeAnnotation(id: string) {
    this.lineChart.getHighcharts().removeAnnotation(id);
  }

  removeElements() {
    // @ts-ignore Property 'annotations' does not exist on type 'Chart'.
    const annotations: Array<Highcharts.Annotation> = this.lineChart.getHighcharts()?.annotations;
    if (annotations) {
      Array.from(annotations).forEach((annotation) => {
        this.removeAnnotation(annotation.userOptions.id);
      });
    }
  }

  private removePlotLine(plotLineId: string) {
    this.lineChart.removePlotLine(plotLineId);
  }

  private renderPlotLine(timestampMs: number, plotLineId: string) {
    this.lineChart.renderPlotLine(timestampMs, plotLineId);
  }

  private renderCreateAnnotationButton(timestampMs: number, active: boolean) {
    const highchart = this.lineChart.getHighcharts();
    const annotation = highchart.addAnnotation({
      id: CREATE_ANNOTATION_ID,
      crop: false,
      events: {
        // required to prevent default callback from highcharts that inserts a form
        click: () => {
          return;
        },
      },
      labels: [
        {
          borderWidth: 0,
          backgroundColor: `rgba(255,255,255,0)`,
          // @ts-ignore Property 'labelOffset' does not exist on type 'Axis'
          distance: -32 - (highchart.xAxis[0].labelOffset || 0) - (highchart.xAxis[0].titleOffset || 0), // position below x-axis labels
          shape: `rect`,
          padding: 0,
          point: {
            x: timestampMs,
            y: highchart.yAxis[0].getExtremes().min,
            xAxis: 0,
            yAxis: 0,
          },
          useHTML: true,
          verticalAlign: `top`,
          text: `<create-annotation-button active="${active}" elref="createAnnotation" />`,
          y: this.lineChart.state.annotations.visibleAnnotations.size ? EXISTING_ANNOTATIONS_BUTTON_Y : 8,
        },
      ],
    });

    annotation.labelsGroup.on(`click`, () => {
      const detail = this.locationDetails(annotation, timestampMs);
      this.showCreateAnnotation(detail);
    });

    return annotation;
  }

  /**
   * NOTE: Previously this was a panel component but highcharts only supports a
   * subset of HTML elements when using the useHtml option.
   * Docs: https://www.highcharts.com/docs/chart-concepts/labels-and-string-formatting#html
   **/
  private viewAnnotationsButtonMarkup(numAnnotations: number, active: boolean, timestampMs: number): string {
    return `<div
      class="view-annotations-button-container ${active ? `active` : ``}"
      id="view-annotations-button-${timestampMs}"
      role="button"
    >
      ${numAnnotations}
    </div>`;
  }

  private renderViewAnnotationsButton(timestampMs: number, active: boolean, numAnnotations: number) {
    const highchart = this.lineChart.getHighcharts();

    const annotation = highchart.addAnnotation({
      crop: false,
      id: `${ANNOTATION_GROUP_BUTTON_ID_PREFIX}-${timestampMs}`,
      events: {
        // required to prevent default callback from highcharts that inserts a form
        click: () => {
          return;
        },
      },
      labels: [
        {
          allowOverlap: true,
          borderWidth: 0,
          backgroundColor: `rgba(255,255,255,0)`,
          // @ts-ignore Property 'labelOffset' does not exist on type 'Axis'.
          distance: -32 - (highchart.xAxis[0].labelOffset || 0) - (highchart.xAxis[0].titleOffset || 0), // position below x-axis labels
          shape: `rect`,
          padding: 0,
          // We've parameterized the point on a value that can change between
          // chart renders. Because of that we make sure to use a callback
          // function so Highcharts can update it as needed on their end.
          point: (updatedAnnotation) => {
            // We want the min of the axes that actually have data to use as
            // the bottom of the chart
            const yAxisAssignment = updatedAnnotation.chart.yAxis[0].series.length ? 0 : 1;
            return {
              x: timestampMs,
              xAxis: 0,
              // Handle the case where there's no data on the primary axis
              y: updatedAnnotation.chart.yAxis[yAxisAssignment].getExtremes().min,
              yAxis: yAxisAssignment,
            };
          },
          useHTML: true,
          verticalAlign: `top`,
          text: this.viewAnnotationsButtonMarkup(numAnnotations, active, timestampMs),
          y: EXISTING_ANNOTATIONS_BUTTON_Y,
        },
      ],
    });

    annotation.labelsGroup.on(`click`, () => {
      const detail = this.locationDetails(annotation, timestampMs);
      this.viewAnnotationsClicked(detail);
    });

    annotation.labelsGroup.on(`mouseenter`, () => {
      const detail = this.locationDetails(annotation, timestampMs);
      this.updateLineChartState({activeAnnotationPreview: detail});
    });

    annotation.labelsGroup.on(`mouseleave`, () => {
      this.updateLineChartState({activeAnnotationPreview: null});
    });
  }

  private timestampToTimeUnitFunction({displayRangeIfWeek = true} = {}) {
    const unit = this.lineChart.props.timeUnit;
    const customFormatting = {day: `ddd, MMM D`};
    return (timestamp) => formatDate(timestamp, {unit, displayRangeIfWeek, customFormatting});
  }

  async showCreateAnnotation(activeAnnotationButtonLocationDetails: ButtonLocationDetails) {
    const date = moment.unix(activeAnnotationButtonLocationDetails.timestampMs / 1000);
    await this.updateLineChartState({
      creatingAnnotation: true,
      createAnnotationDate: date.format(`MMM D, YYYY HH:mm:ss`),
      activeAnnotationButton: activeAnnotationButtonLocationDetails,
      viewingAnnotations: false,
    });
  }

  /**
   * This method is async because it uses the setState method from the lineChart
   * which is async. If you want lineChart.state to be updated make sure to wait
   * for the returned promise to resolve.
   * @param state
   * @returns
   */
  updateLineChartState(state: Partial<AnnotationState>): Promise<void> {
    return new Promise((resolve) => {
      const annotations = Object.assign({}, this.lineChart.state.annotations, state);
      this.lineChart.setState({annotations}, () => {
        resolve();
      });
    });
  }

  async viewAnnotationsClicked(detail: ButtonLocationDetails) {
    await this.updateLineChartState({
      creatingAnnotation: false,
      viewingAnnotations: true,
      activeAnnotationButton: detail,
      activeAnnotationPreview: null,
    });

    const activeAnnotationGroup = this.lineChart.state.annotations.visibleAnnotations.get(
      this.lineChart.state.annotations.activeAnnotationButton.timestampMs,
    );

    track(TrackingEvent.Read, {
      [TrackingEventProperty.AnnotationText]: activeAnnotationGroup
        .map((annotation) => annotation.description)
        .join(`, `),
      [TrackingEventProperty.AnnotationTimestamp]: moment(activeAnnotationGroup[0].date).format(`YYYY-MM-DD HH:mm:ss`),
      [TrackingEventProperty.ReportName]: getReportName(),
      [TrackingEventProperty.AnnotationCount]: activeAnnotationGroup.length,
    });
  }
}
